// Case Resolution System Types

import { MessageImage } from './message';
import type { CaseReason, CaseSource, SenderRole, ThreadMessageKind, TradeRole } from './thread';
import type { HaveFinish } from 'utils/finishes';

export enum CaseStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  /** Written by the due-date cron when the trade is already terminal; reopenable like RESOLVED. */
  CLOSED = 'CLOSED'
}

/** A case that takes no more actions until it is reopened (Asana 1219265535165249). */
export const isTerminalCaseStatus = (status: CaseStatus): boolean =>
  status === CaseStatus.RESOLVED || status === CaseStatus.CLOSED;

export enum AdminActionType {
  COMPLETE_TRADE = 'COMPLETE_TRADE',
  CANCEL_TRADE = 'CANCEL_TRADE',
  REFUND_BOTH = 'REFUND_BOTH',
  NO_ACTION = 'NO_ACTION',
  SET_DUE_DATE = 'SET_DUE_DATE',
  MESSAGE_SENT = 'MESSAGE_SENT',
  CASE_REOPENED = 'CASE_REOPENED'
}

export interface Case {
  id: string;
  tradeId: string;
  /** The trade's TRADE thread since change 2; the CASE thread on legacy cases. */
  conversationId: string;
  /** Set on new-path cases; moves on every reopen. */
  dividerTimestamp?: string;
  status: CaseStatus;
  createdAt: string;
  updatedAt: string;
  dueDate: string | null;
  summonedBy: number;
  alsoRequestedBy: number | null;
  senderUsername: string;
  receiverUsername: string;
  tradeNumber: string;
  /**
   * Reopen support (Asana 1219265535165249, backend PR 370). The keys are always
   * present on list rows and detail; their absence means an older backend, and
   * the Reopen button stays hidden. `allowedActions` is server-computed: an
   * action missing from it renders disabled (status-only gating is the fallback
   * when the key is absent).
   */
  reopenedAt?: string | null;
  reopenedBy?: { id: number; username: string } | string | null;
  allowedActions?: string[];
  /**
   * Summon Admin wizard fields (Asana 1219952262403623). Legacy cases carry
   * reason null and empty affectedItemIds. caseNumber is the raw integer;
   * render it through formatCaseNumber (utils/caseNumber).
   */
  caseNumber?: number;
  reason?: CaseReason | null;
  affectedItemIds?: string[];
  description?: string | null;
  images?: MessageImage[];
  summonedByRole?: TradeRole;
  source?: CaseSource | null;
  /** Admin queue rows (typed threads): who filed, resolved to a user. */
  summonedByUser?: { id: number; username: string } | null;
  trade?: {
    createdAt: string;
    sender?: {
      id: number;
      username: string;
      avatarUrl?: string;
    };
    receiver?: {
      id: number;
      username: string;
      avatarUrl?: string;
    };
  };
}

export interface CaseListResponse {
  cases: Case[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCases: number;
    pageSize: number;
  };
}

export interface CaseMessage {
  id: string;
  content: string;
  senderId: number;
  senderUsername: string;
  receiverId: number;
  createdAt: string;
  readAt: string | null;
  sender: {
    id: number;
    username: string;
    avatarUrl?: string;
    isAdminMessage: boolean;
  };
  images?: MessageImage[];
  /** Case phase row (change 2): the admin stream renders these as events. */
  kind?: ThreadMessageKind | null;
  senderRole?: SenderRole;
}

export interface AdminAction {
  id: string;
  caseId: string;
  adminId: number | null;
  adminUsername: string | null;
  actionType: AdminActionType;
  details: Record<string, any> | null;
  createdAt: string;
}

// Party (sender/receiver) shape on the admin case-detail trade.
// Avatar enrichment fields (country/isElite/border*) are supplied by the
// backend case-detail response so AvatarWithFlag can render flags + Elite
// treatment consistently with Trade Alerts. `country` is mapped from the
// user's shippingCountry on the backend.
export interface TradePartyDetails {
  id: number;
  username: string;
  email?: string;
  avatarUrl?: string;
  country?: string | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

export interface TradeDetails {
  id: string;
  status: string;
  tradeNumber: string;
  createdAt: string;
  totalValue: number;
  // Single timestamp set when the sender marks the trade shipped (no per-side field).
  shippedAt?: string | null;
  sender: TradePartyDetails;
  receiver: TradePartyDetails;
  items: Array<{
    id?: string;
    name: string;
    setCode?: string;
    setName?: string;
    set?: string;
    quantity: number;
    condition: string;
    pointValue: number;
    foil?: boolean;
    language?: string;
    edition?: string;
    scryfallID?: string;
    collectorNumber?: string;
    rarity?: string;
    /** Authoritative finish; `foil` is its mirror. Absent on an older backend. */
    finish?: HaveFinish | null;
    completedAt?: string | null;  // set when the card has been individually completed (sender credited)
    removedAt?: string | null;    // set when the card was cancelled per-card (receiver refunded)
  }>;
}

export interface CaseDetailResponse {
  case: Case & {
    trade: TradeDetails;
  };
  messages: CaseMessage[];
  adminActions: AdminAction[];
  dividerTimestamp: string;
  conversationId: string;
}

/**
 * GET /trades/:tradeId/case. The live backend answers FLAT { exists, caseId,
 * status, createdAt } (TradeController.ts:709-714, verified 2026-10-28); the
 * nested `case`, `summonedBy`, `alsoRequestedBy` and `summonedAt` fields only
 * ever existed in this type, so the Contact modal's status block showed no
 * names. Backend group 5 keeps the flat fields and adds the nested ones plus
 * caseNumber (raw integer) and conversationId (the CASE thread). Typed as that
 * superset: flat required when a case exists, everything else optional.
 */
export interface TradeCaseStatusResponse {
  exists: boolean;
  caseId?: string;
  status?: CaseStatus;
  createdAt?: string;
  case?: {
    id: string;
    status: CaseStatus;
    createdAt: string;
    dueDate: string | null;
  };
  summonedBy?: string;
  alsoRequestedBy?: string | null;
  summonedAt?: string;
  caseNumber?: number;
  /** The trade's TRADE thread since change 2 (the CASE thread before it). */
  conversationId?: string;
  dividerTimestamp?: string;
}

export interface SummonAdminResponse {
  caseId: string;
  status: CaseStatus;
  message: string;
  /** The thread the case lives in: the trade's TRADE thread since change 2. */
  conversationId: string;
  caseNumber?: number;
}

export interface CaseBadgeCountResponse {
  unreadCount: number;
}

export interface AdminActionRequest {
  actionType: AdminActionType;
  details?: {
    dueDate?: string;
    notes?: string;
  };
}

export interface AdminActionResponse {
  success: boolean;
  case: {
    id: string;
    status: CaseStatus;
    updatedAt: string;
  };
}

export interface SendAdminMessageRequest {
  content: string;
}

export interface SendAdminMessageResponse {
  success: boolean;
}

export interface SetDueDateRequest {
  dueDate: string;
}

export interface SetDueDateResponse {
  success: boolean;
  case: {
    id: string;
    dueDate: string;
    status: CaseStatus;
  };
}
